import { useCallback, useState } from 'react';
import { useChat } from '../../../context/chat/ChatContext';
import { fetchGraphQL } from '../gql.client';
import { createOrderMutation } from '../gql.mutations';

import { useCart } from '../../../context/cart/CartContext';
import { useToastContext } from '../../../context/toast/ToastContext';
import type { UseOrderHook, UseOrderHookLoadings } from '../../../types/Order.type';
import { mobileAndTabletCheck } from '../../../utils/device';

export const useOrders = (): UseOrderHook => {
    const { user, deliveryDate } = useChat();
    const { cart } = useCart();
    const { showToast } = useToastContext();

    const [error, setError] = useState<string | null>(null);
    const [orderState, setOrderState] = useState<UseOrderHookLoadings>({
        createOrder: {
            loading: false,
            success: null,
            error: null,
            data: null
        },
    });

    const createOrder = useCallback(async () => {
        if (user === null) return;

        setError(null);
        setOrderState(prev => ({
            ...prev,
            createOrder: {
                data: null,
                error: null, 
                loading: true,
                success: null, 
            }
        }));

        const selectedDeliveryDays = user.deliveryDays;
        const subscriptionOriginType = mobileAndTabletCheck()
            ? 'customer_web_mobile'
            : 'customer_web_desktop';

        try {
            const resp = await fetchGraphQL({
                url: `${process.env.REACT_APP_SUBSCRIPTION_API_URL}/graphql/user`,
                query: createOrderMutation(),
                variables: {
                    order: {
                        deliveryDate: deliveryDate as string,
                        start: selectedDeliveryDays[0].time_start,
                        end: selectedDeliveryDays[0].time_end,
                        products: cart.map((item) => ({
                            batch_id: item.batchId,
                            id: item.id,
                            inventoryId: item.inventoryId,
                            qty: item.quantity
                        })),
                        tip: 0,
                    },
                    origin: subscriptionOriginType,
                },
            });

            if ((resp as any)?.data) {

                const hasError = (resp as any)?.data?.createOrder.error;
                if (hasError) {
                    setError(hasError);
                    showToast({ message: hasError, error: true });
                }

                const orderId = (resp as any)?.data?.createOrder?.id;
                if (orderId) {
                    setOrderState(prev => ({
                        ...prev,
                        createOrder: {
                            data: {
                                id: orderId
                            },
                            error: null,
                            loading: false,
                            success: true,
                        }
                    }));

                    showToast({ message: 'Order updated success', success: true });
                }
            }
        } catch (err) {
            const genericError = 'An error occurred. Please try again later.';
            setError(genericError);
            showToast({ message: genericError, error: true, });
        }
    }, [cart, deliveryDate, showToast, user]);

    return {
        error,
        orderState,
        createOrder,
    };
};